<template>
  <div class="editor-embed">
    <div v-if="showSkuSwitcher && !activeMeta.skuConfirmed" class="sku-confirmation-shield"></div>
    <TaskSkuSwitcher
      v-if="showSkuSwitcher"
      class="editor-sku-switcher"
      :order-id="activeMeta.orderId"
      :skus="activeMeta.taskSkus"
      :active-sku-id="activeMeta.skuId"
      :confirmed="Boolean(activeMeta.skuConfirmed)"
      :switching="skuSwitching"
      @confirm="confirmTaskSku"
    />
    <div v-if="loading" class="embed-loading">
      <WhaleLoading
        :fullscreen="false"
        :custom-tip="loadingTip"
      />
    </div>
    <div v-else-if="loadError" class="embed-error">
      <strong>编辑器加载失败</strong>
      <p>{{ loadError }}</p>
      <button type="button" @click="retryEditor">重新加载</button>
    </div>
    <iframe
      :key="frameKey"
      ref="frameRef"
      class="editor-frame"
      :src="iframeSrc"
      title="鲸夕智绘编辑器"
      @load="onFrameLoad"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import WhaleLoading from '@/components/common/WhaleLoading.vue';
import { usePlatformStore } from '@/composables/usePlatformStore';
import {
  buildEditorSyncPayload,
  cloneForPostMessage,
  pickEditorBootstrapMeta,
  EDITOR_DATA_SYNC_EVENT,
} from '@/utils/editorBridge';
import { cdpUrl } from '@/utils/platformHelpers';
import { isUnitTestRuntime } from '@/utils/runtimeEnv';
import TaskSkuSwitcher from '@/components/editor/TaskSkuSwitcher.vue';

const props = defineProps({
  meta: { type: Object, default: null },
});

const {
  closeEditor,
  reloadFromStorage,
  session,
  navigate,
  editorMeta,
  platformFonts,
  editorSyncContext,
  handleEditorApiRequest,
  ensureFontsLoaded,
  refreshTemplateLibrary,
  switchTaskSkuFromEditor,
  showToast,
  loadOrganizationStores,
  refreshModuleApi,
} = usePlatformStore();
const activeMeta = computed(() => {
  const storeMeta = editorMeta.value || {};
  const propMeta = props.meta || {};
  const merged = { ...storeMeta, ...propMeta };
  // 父组件有时只传入路由元数据，真正的 layerJson 会在后台恢复后写入 store。
  // 不能让这份旧 prop 覆盖已恢复的完整分层数据。
  if (!merged.layerJson && storeMeta.layerJson) merged.layerJson = storeMeta.layerJson;
  return Object.keys(merged).length ? merged : { mode: 'studio', isNew: true };
});
const showSkuSwitcher = computed(() => Boolean(
  activeMeta.value?.fromTaskId && activeMeta.value?.taskSkus?.length,
));
const loadingMessage = computed(() => activeMeta.value?.layerJson
  ? '正在恢复模板图层，请稍候…'
  : '正在加载完整编辑器…');
const loadingStage = ref('');
const loadingTip = computed(() => loadingStage.value || (
  activeMeta.value?.layerJson
    ? '正在恢复模板图层，请稍候…'
    : (activeMeta.value?.templateName ? `正在加载【${activeMeta.value.templateName}】模版，请稍候…` : loadingMessage.value)
));
const loading = ref(true);
const loadError = ref('');
const frameKey = ref(0);
const frameRef = ref(null);
const skuSwitching = ref(false);
let readyTimer = null;
let bootstrapRetryTimer = null;
let pendingSkuSwitch = null;
let skuSwitchTimer = null;

function clearBootstrapRetry() {
  if (bootstrapRetryTimer) {
    clearInterval(bootstrapRetryTimer);
    bootstrapRetryTimer = null;
  }
}

function scheduleBootstrapRetry() {
  clearBootstrapRetry();
  let attempts = 0;
  bootstrapRetryTimer = setInterval(() => {
    if (!loading.value || attempts >= 3) {
      clearBootstrapRetry();
      return;
    }
    attempts += 1;
    syncAllToFrame();
  }, 800);
}

const LAYER_JSON_BRIDGE_PREFIX = 'jx_editor_layer_json_';
const MAX_URL_PARAM_LEN = 500;

function buildIframeSrc() {
  const m = activeMeta.value;
  const q = new URLSearchParams({
    vueEmbed: '1',
    role: session.role || 'designer',
    store: m.storeId || session.storeId || 's1',
    cdpV: import.meta.env.VITE_CDP_BUILD_VERSION || 'studio203',
  });
  if (m.templateId || m.fromTemplate) {
    q.set('tpl', m.templateId || m.fromTemplate);
    // layerJson 仅通过 postMessage / localStorage 桥传递，避免 URL 过长导致 iframe 无法加载
    if (m.templateName) q.set('title', m.templateName);
    if (m.editMasterTemplate) q.set('editMaster', '1');
    const thumb = m.thumb && !String(m.thumb).startsWith('data:') ? String(m.thumb) : '';
    if (thumb && thumb.length <= MAX_URL_PARAM_LEN) q.set('thumb', thumb);
  } else if (m.id && !m.isNew) {
    q.set('design', m.id);
  } else if (m.standalone || m.mode === 'studio') {
    q.set('mode', 'studio');
  } else if (m.skipAutoTemplate || m.fromTaskId) {
    q.set('mode', 'task');
    if (m.orderId) q.set('orderId', m.orderId);
    if (m.fromTaskId) q.set('taskId', m.fromTaskId);
    if (m.templateName) q.set('title', m.templateName);
    if (m.id) q.set('designId', m.id);
  } else if (m.thumb && m.isNew) {
    q.set('mode', 'studio');
  } else if (m.isNew) {
    q.set('mode', 'task');
    if (m.orderId) q.set('orderId', m.orderId);
  }
  return `${cdpUrl('platform.html')}?${q.toString()}`;
}

// iframe 的 src 一旦变化，编辑器会整体重载。模版名与封面会被后台分层解析结果回填，
// 若直接用 computed 会在加载途中重置 iframe，表现为「模板打不开」。
// 因此只在编辑对象本身切换或用户点重新加载时重建地址。
const frameIdentity = computed(() => {
  const m = activeMeta.value;
  return [
    m.templateId || m.fromTemplate || '',
    m.id || '',
    m.mode || '',
    m.fromTaskId || '',
    m.storeId || session.storeId || '',
    m.editMasterTemplate ? 'master' : '',
    frameKey.value,
  ].join('|');
});

const iframeSrc = ref('');
watch(frameIdentity, () => {
  iframeSrc.value = buildIframeSrc();
}, { immediate: true });

// 后台分层解析完成后补推 bootstrap；iframe 侧检测到画布仍为空会据此装载图层。
watch(() => activeMeta.value?.layerJson, (layerJson, prev) => {
  if (!layerJson || layerJson === prev) return;
  if (!frameRef.value?.contentWindow) return;
  syncEditorBootstrapToFrame();
});

function buildSyncPayload() {
  const ctx = editorSyncContext();
  return buildEditorSyncPayload({
    materials: ctx.materials,
    materialFolders: ctx.materialFolders,
    platformFonts: platformFonts.value,
    useRemoteMaterials: ctx.useRemoteMaterials,
    deletedDefaultMaterialIds: ctx.deletedDefaultMaterialIds,
    templates: ctx.templates,
    templateFolders: ctx.templateFolders,
    useRemoteTemplates: ctx.useRemoteTemplates,
    useRemoteFonts: ctx.useRemoteFonts,
    useRemoteDesignCenter: ctx.useRemoteDesignCenter,
    stores: ctx.stores,
    organizationTree: ctx.organizationTree,
    currentStoreId: ctx.currentStoreId,
  });
}

function postToFrame(message) {
  const target = frameRef.value?.contentWindow;
  if (!target) return;
  const payload = cloneForPostMessage(message);
  target.postMessage(payload, '*');
}

function finishSkuSwitch() {
  if (!pendingSkuSwitch) return;
  const sku = pendingSkuSwitch.sku;
  pendingSkuSwitch = null;
  clearTimeout(skuSwitchTimer);
  skuSwitchTimer = null;
  const result = switchTaskSkuFromEditor(sku);
  skuSwitching.value = false;
  if (result?.sameDesign) {
    postToFrame({
      type: 'cdp-task-sku-confirmed',
      meta: editorBootstrapMeta(),
    });
  }
}

function confirmTaskSku(sku) {
  if (!sku || skuSwitching.value) return;
  if (String(activeMeta.value?.skuId || '') === String(sku.id || '')) {
    switchTaskSkuFromEditor(sku);
    postToFrame({ type: 'cdp-task-sku-confirmed', meta: editorBootstrapMeta() });
    return;
  }
  skuSwitching.value = true;
  const requestId = `sku_${Date.now()}`;
  pendingSkuSwitch = { requestId, sku };
  postToFrame({ type: 'cdp-task-sku-switch-request', requestId, sku });
  skuSwitchTimer = setTimeout(() => {
    if (!pendingSkuSwitch || pendingSkuSwitch.requestId !== requestId) return;
    showToast('当前 SKU 草稿云端同步较慢，已使用本地草稿继续切换', 'warn');
    finishSkuSwitch();
  }, 15_000);
}

function normalizeLayerJson(raw) {
  if (raw == null || raw === '') return raw;
  if (typeof raw === 'string') return raw;
  try {
    return JSON.stringify(raw);
  } catch {
    return '';
  }
}

function stashLargeLayerJson(meta = {}) {
  const layerJson = normalizeLayerJson(meta.layerJson);
  const next = { ...meta, layerJson };
  if (typeof layerJson !== 'string' || layerJson.length <= 8000) return next;
  const key = `${LAYER_JSON_BRIDGE_PREFIX}${meta.templateId || meta.fromTemplate || meta.id || frameKey.value}`;
  try {
    localStorage.setItem(key, layerJson);
    return { ...next, layerJson: '', layerJsonRef: key };
  } catch {
    return next;
  }
}

function editorBootstrapMeta() {
  const raw = activeMeta.value || {};
  const stableId = raw.id
    || raw.templateId
    || raw.fromTemplate
    || (raw.mode === 'studio' ? `studio_${frameKey.value}` : `d_${frameKey.value}`);
  return cloneForPostMessage(stashLargeLayerJson(pickEditorBootstrapMeta({ ...raw, id: stableId })));
}

function isFrameMessage(event) {
  if (!event?.data) return false;
  const frameWindow = frameRef.value?.contentWindow;
  if (frameWindow && event.source === frameWindow) return true;
  const type = event.data?.type;
  if (typeof type !== 'string' || !type.startsWith('cdp-')) return false;
  try {
    return !!window.location.origin && event.origin === window.location.origin;
  } catch {
    return false;
  }
}

function editorReadyTimeoutMs() {
  const meta = activeMeta.value || {};
  return meta.layerJson || meta.templateId || meta.fromTemplate || (meta.id && !meta.isNew)
    ? 180_000
    : 60_000;
}

function scheduleReadyTimeout(timeoutMs = editorReadyTimeoutMs(), message = '') {
  clearTimeout(readyTimer);
  readyTimer = setTimeout(() => {
    if (!loading.value) return;
    loading.value = false;
    loadError.value = message || '编辑器初始化超时，请重新加载；本次模板数据不会丢失。';
  }, timeoutMs);
}

function syncAllToFrame() {
  try {
    const payload = buildSyncPayload();
    syncPlatformStorageToFrame();
    syncPlatformDataToFrame(payload);
    syncFontLibraryToFrame(payload);
    syncEditorBootstrapToFrame();
  } catch (error) {
    if (import.meta.env.DEV) console.warn('[editor-embed] 同步编辑器数据失败', error);
    loadError.value = error?.message || '编辑器数据同步失败，请重新加载';
    loading.value = false;
  }
}

function onFrameLoad() {
  loadError.value = '';
  loading.value = true;
  loadingStage.value = '正在启动编辑器…';
  try {
    const cdpBase = import.meta.env.VITE_CDP_ENGINE_URL || import.meta.env.VITE_PDF_SERVICE_URL;
    if (cdpBase && frameRef.value?.contentWindow) {
      frameRef.value.contentWindow.__CDP_ENGINE_BASE__ = String(cdpBase).replace(/\/+$/, '');
    }
  } catch { /* cross-origin iframe */ }
  syncAllToFrame();
  scheduleBootstrapRetry();
  if (!loadError.value) scheduleReadyTimeout();
}

function retryEditor() {
  clearTimeout(readyTimer);
  clearBootstrapRetry();
  loadError.value = '';
  loading.value = true;
  loadingStage.value = '';
  frameKey.value += 1;
}

function onEditorDataSync(event) {
  if (!frameRef.value?.contentWindow) return;
  const payload = event?.detail;
  if (!payload) {
    syncAllToFrame();
    return;
  }
  postToFrame({ type: 'cdp-platform-data-sync', ...payload });
  postToFrame({ type: 'cdp-font-library-updated', fonts: payload.fonts || [] });
}

function syncPlatformStorageToFrame() {
  postToFrame({ type: 'cdp-platform-storage-sync' });
}

function syncPlatformDataToFrame(payload = buildSyncPayload()) {
  postToFrame({
    type: 'cdp-platform-data-sync',
    ...payload,
  });
}

function syncFontLibraryToFrame(payload) {
  const resolvedPayload = Array.isArray(payload?.fonts) ? payload : buildSyncPayload();
  postToFrame({
    type: 'cdp-font-library-updated',
    fonts: resolvedPayload.fonts,
  });
}

function syncEditorBootstrapToFrame() {
  postToFrame({
    type: 'cdp-editor-bootstrap',
    version: 1,
    meta: editorBootstrapMeta(),
  });
}

function onMessage(event) {
  if (!isFrameMessage(event) || !event?.data) return;
  if (event.data.type === 'cdp-editor-ready') {
    clearTimeout(readyTimer);
    clearBootstrapRetry();
    loadError.value = '';
    loadingStage.value = '';
    loading.value = false;
    return;
  }
  if (event.data.type === 'cdp-editor-iframe-ready') {
    loadingStage.value = activeMeta.value?.templateId || activeMeta.value?.fromTemplate
      ? '正在读取模板分层数据…'
      : '正在初始化画布…';
    scheduleReadyTimeout();
    syncAllToFrame();
    return;
  }
  if (event.data.type === 'cdp-editor-progress') {
    if (event.data.message) loadingStage.value = event.data.message;
    scheduleReadyTimeout();
    return;
  }
  if (event.data.type === 'cdp-task-sku-switch-ready') {
    if (pendingSkuSwitch?.requestId === event.data.requestId) finishSkuSwitch();
    return;
  }
  if (event.data.type === 'cdp-editor-error') {
    clearTimeout(readyTimer);
    loading.value = false;
    loadError.value = event.data.message || '模板数据未能载入编辑器';
    return;
  }
  if (event.data.type === 'cdp-storage-updated') {
    reloadFromStorage();
    return;
  }
  if (event.data.type === 'cdp-editor-close') {
    closeEditor(event.data.returnView);
    return;
  }
  if (event.data.type === 'cdp-open-fonts') {
    closeEditor('fonts');
    return;
  }
  if (event.data.type === 'cdp-api-request') {
    const { requestId, action, payload } = event.data;
    if (!requestId || !action) return;
    if (action === 'templates.resolve') {
      loadingStage.value = '正在获取模板分层数据…';
      scheduleReadyTimeout();
    }
    const target = frameRef.value?.contentWindow;
    handleEditorApiRequest(action, payload || {})
      .then((data) => {
        target?.postMessage(cloneForPostMessage({
          type: 'cdp-api-response',
          requestId,
          ok: true,
          data,
        }), '*');
      })
      .catch((error) => {
        target?.postMessage(cloneForPostMessage({
          type: 'cdp-api-response',
          requestId,
          ok: false,
          error: error?.message || '请求失败',
        }), '*');
      });
  }
}

onMounted(async () => {
  window.addEventListener('message', onMessage);
  window.addEventListener('cdp-local-storage-updated', syncAllToFrame);
  window.addEventListener('cdp-font-library-updated', syncFontLibraryToFrame);
  window.addEventListener(EDITOR_DATA_SYNC_EVENT, onEditorDataSync);
  scheduleReadyTimeout(45_000, '编辑器资源加载超时，请检查网络后重新加载；本次模板数据不会丢失。');

  // 页面刷新或直接进入时预热公共数据；当前模版由 iframe 桥接单独解析，避免重复请求。
  // 素材使用非 force 加载：openEditor() 已预热过数据（30s 缓存 TTL 内），
  // 避免 queryMaterial 被重复调用两次。页面刷新时缓存为空仍会正常请求。
  if (!isUnitTestRuntime()) {
    try {
      await Promise.all([
        loadOrganizationStores?.().catch(() => {}),
        refreshModuleApi?.('materials', { force: false }).catch(() => {}),
        refreshTemplateLibrary?.().catch(() => {}),
        ensureFontsLoaded?.().catch(() => {}),
      ]);
      syncAllToFrame();
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[CloudEditorView] 刷新预热店铺/素材/模版/字体异常', err);
    }
  }
});

onUnmounted(() => {
  clearTimeout(readyTimer);
  clearTimeout(skuSwitchTimer);
  clearBootstrapRetry();
  window.removeEventListener('message', onMessage);
  window.removeEventListener('cdp-local-storage-updated', syncAllToFrame);
  window.removeEventListener('cdp-font-library-updated', syncFontLibraryToFrame);
  window.removeEventListener(EDITOR_DATA_SYNC_EVENT, onEditorDataSync);
});
</script>

<style scoped>
.editor-embed {
  width: 100vw;
  height: 100vh;
  position: relative;
  background: #e8edf3;
}
.editor-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}
.sku-confirmation-shield {
  position: absolute;
  inset: 56px 0 0;
  z-index: 4;
  background: rgba(246, 248, 250, .16);
  cursor: not-allowed;
}
.editor-sku-switcher {
  position: absolute;
  top: 10px;
  left: clamp(132px, 10vw, 168px);
  z-index: 6;
  max-width: min(680px, calc(100vw - 760px));
}
.embed-loading {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #fafafa;
  color: #1f1f1f;
  pointer-events: none;
}
.embed-error {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  background: #fafafa;
  color: #1f2937;
  text-align: center;
}
.embed-error strong { font-size: 18px; }
.embed-error p { max-width: 520px; margin: 0; color: #64748b; font-size: 13px; line-height: 1.6; }
.embed-error button { padding: 9px 18px; border-radius: 8px; background: #6d28d9; color: #fff; font-weight: 700; }
.embed-loading .hint {
  font-size: 12px;
  color: #888;
}
.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e8e8e8;
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
@media (max-width: 1180px) {
  .editor-sku-switcher {
    left: 132px;
    width: min(460px, calc(100vw - 520px));
  }
}
</style>
